import { client, ErrorResponse, isErrorPayload } from "@/utils/uno-axios";
import { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { endSession } from "@/utils/token-refresher";

export type AxiosRequestConfigWithRequest<RequestData> =
  AxiosRequestConfig<RequestData> & RequestData;

export function isErrorResponse(
  item: ErrorResponse | any
): item is ErrorResponse {
  if (item !== undefined) {
    const i = item as ErrorResponse;
    return i.error !== undefined && isErrorPayload(i.error);
  }
  return false;
}

export function isCredentialError(response: ErrorResponse) {
  const { error } = response;
  // JWT_TOKEN_EXPIRED / JWT_TOKEN_INVALIDATED가 여기까지 온다는 건 인터셉터가 이미
  // 갱신과 재시도를 끝낸 뒤라는 뜻이다. 더 해볼 게 없으니 세션을 끝낸다.
  // 이게 빠져 있어서, 갱신이 조용히 실패한 세션은 로그아웃되지 않고 401만 반복해서 떴다.
  //
  // 403(인증은 됐는데 그 자원에 권한이 없음)은 세션을 끝내지 않는다(token-refresher 의 isSessionEndedError 와 같은 기준).
  // 예전에는 여기서 403 도 '권한이 없습니다' 얼럿과 함께 로그아웃시켜, 바이어가 볼 수 없는(미발행·미공유)
  // 라인시트 세트 링크를 메일에서 눌러도 로그아웃됐다(2026-10-19). 403 은 호출부가 받아 안내 화면
  // (존재하지 않는 라인시트 등)이나 토스트로 처리한다.
  return (
    error.code === 401 &&
    (error.status === "UNAUTHENTICATED" ||
      error.status === "JWT_TOKEN_REFRESH_FAILED" ||
      error.status === "JWT_TOKEN_EXPIRED" ||
      error.status === "JWT_TOKEN_INVALIDATED")
  );
}

/*
 * react-router 가 hook만 제공하니까, hook관련된 애는 이친구가 하고, http 통신의 책임은 uno-axios로 분리
 * */

export default function useFetcher<RequestQueryOrData, ResponseData>() {
  const [error, setError] = useState<ErrorResponse>();

  useEffect(() => {
    if (error) {
      endSession();
    }
  }, [error]);

  return useCallback(
    async (config: AxiosRequestConfig<RequestQueryOrData>) => {
      return client<ResponseData, AxiosResponse<ResponseData>>(config)
        .then((r) => {
          return r.data as ResponseData;
        })
        .catch((err: AxiosError<ErrorResponse | any>) => {
          if (isErrorResponse(err) && isCredentialError(err)) {
            // Cookies.remove("access_token");
            // Cookies.remove("refresh_token");
            // if (getLocation().pathname !== "/auth/sign-in") {
            //   alert("!!");
            //   navigate("/auth/sign-in");
            // }
            setError(err);
          }

          throw err;
        });
    },
    [setError]
  );
}
